/**
 * Swiss number and currency formatting.
 *
 * Amounts are always rendered the Swiss way, whatever the UI language: a point as
 * decimal separator and an apostrophe as thousands separator (e.g. `12'345.60 CHF`).
 * Never derive these separators from the device locale: a French or German locale
 * would render a comma, which is not how amounts are written in Switzerland.
 */

export const defaultCurrency = "CHF";

/** Decimal separator used for every amount displayed in the app. */
export const DECIMAL_SEPARATOR = ".";

/** Thousands separator used for every amount displayed in the app. */
export const THOUSAND_SEPARATOR = "'";

/** Below this value no thousands separator is added (e.g. `9999.00`, but `10'000.00`). */
const THOUSAND_SEPARATOR_THRESHOLD = 10_000;

/**
 * Turns a user typed number into its canonical form, so that it can safely be
 * given to `Number.parseFloat`. Keyboards in a French or German locale emit a
 * comma as decimal separator, which `Number.parseFloat` silently truncates
 * (`parseFloat("25,50") === 25`).
 * @param text - The raw text typed by the user
 * @returns The same text with commas replaced by the canonical decimal separator
 */
export const normalizeDecimalSeparator = (text: string) => text.replace(/,/g, DECIMAL_SEPARATOR);

/**
 * Format a number with two decimals, and an apostrophe as thousands separator
 * for numbers >= 10000.
 * @param value - The number to format
 * @param decimalSeparator - The decimal separator
 * @param thousandSeparator - The thousand separator
 * @returns The formatted string, e.g. `-12'345.60`
 */
export const formatNumber = ({
    value,
    decimalSeparator = DECIMAL_SEPARATOR,
    thousandSeparator = THOUSAND_SEPARATOR,
}: {
    value: number;
    decimalSeparator?: string;
    thousandSeparator?: string;
}) => {
    const absoluteValue = Math.abs(value);

    const sign = value < 0 ? "-" : "";
    const parts = absoluteValue.toString().split(".");
    let integerPart = parts[0];
    const decimalPart = (parts[1] || "00").padEnd(2, "0");

    if (absoluteValue >= THOUSAND_SEPARATOR_THRESHOLD) {
        // Add apostrophe every 3 digits from right to left
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    }
    return sign + integerPart + decimalSeparator + decimalPart;
};

/**
 * Amount as a foreign locale renders it, e.g. `1 234,50 CHF`. The trailing currency code is
 * what tells an amount apart from free text, and the thousands may be grouped with any of the
 * spaces a French locale uses (no-break, narrow no-break, thin).
 *
 * Only one or two decimals are matched, so that a thousands separator written the English way
 * (`1,000`) is never mistaken for a decimal one. The backend renders its amounts through Babel,
 * which always gives a Swiss franc two decimals.
 */
const FOREIGN_AMOUNT_PATTERN = new RegExp(
    "(\\d[\\d\\u00a0\\u202f\\u2009]*),(\\d{1,2})(?=\\s?[A-Z]{3}\\b)",
    "g",
);

/**
 * Rewrite the amounts embedded in a text built by the backend into the Swiss notation.
 *
 * Some labels reach the app already rendered server side, e.g. the detail of a fee line
 * `km voiture privé [200.0 × 0,18 CHF]`. The backend formats the amounts it embeds with a
 * French locale, so displaying them as-is contradicts every other amount of the same screen.
 *
 * The free text the label starts with is left untouched: an employer typing `3,5` in a fee
 * name is not writing an amount.
 * @param text - The text as received from the backend
 * @returns The same text with its amounts written the Swiss way
 */
export const normalizeAmountsInText = (text: string) =>
    text.replace(FOREIGN_AMOUNT_PATTERN, (amount, integerPart: string, decimals: string) => {
        const value = Number(`${integerPart.replace(/\D/g, "")}.${decimals}`);

        return isNaN(value) ? amount : formatNumber({ value });
    });

export type FormatCurrencyType = {
    amountInCents: number;
    displayDigitsWhenNoCents?: boolean;
    currency?: string;
    displayCurrencySymbol?: boolean;
};

/**
 * Format an amount held in cents, e.g. `1234560` -> `12'345.60 CHF`.
 * @param amountInCents - The amount, in cents
 * @param displayDigitsWhenNoCents - When false, a round amount ends with `.–` instead of `.00`
 * @param currency - The currency code appended to the amount
 * @param displayCurrencySymbol - When false, the currency code is omitted
 * @returns The formatted amount, with a non-breaking space before the currency
 */
export const formatCurrency = ({
    amountInCents,
    displayDigitsWhenNoCents = true,
    currency = defaultCurrency,
    displayCurrencySymbol = true,
}: FormatCurrencyType) => {
    const amountInCurrency = Math.round(amountInCents) / 100;

    let amount = formatNumber({ value: amountInCurrency });

    if (!displayDigitsWhenNoCents) {
        amount = amount.replace(`${DECIMAL_SEPARATOR}00`, `${DECIMAL_SEPARATOR}–`);
    }

    return `${amount}${displayCurrencySymbol ? `\xa0${currency}` : ""}`;
};
